import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import jwt_decode from "jwt-decode";
import axios from "axios";
import { useAtom } from "jotai";
import { gbState } from "../../components/shared/GlobalState";

const WARNING_SECONDS = 180; // 3 minutes

const SessionTimeoutModal = () => {
  const navigate = useNavigate();
  const [userStatus, setUserStatus] = useAtom(gbState);
  const [showModal, setShowModal] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Refs to stabilize the interval — avoids recreating it on every render
  const clearSessionRef = useRef(null);
  const showModalRef = useRef(false);
  const modalShownForExpRef = useRef(null);

  // Keep showModalRef in sync
  showModalRef.current = showModal;

  // Stable clearSession stored in ref
  useEffect(() => {
    clearSessionRef.current = () => {
      localStorage.removeItem("token");
      sessionStorage.removeItem("user-details");
      setUserStatus({ ...userStatus, user: null });
      setShowModal(false);
      navigate("/login", { replace: true });
    };
  }, [navigate, setUserStatus, userStatus]);

  // Single stable interval — reads from refs to avoid dependencies
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem("token");
      if (!token) {
        if (showModalRef.current) setShowModal(false);
        return;
      }

      try {
        const decoded = jwt_decode(token);
        const pathname = window.location.pathname;

        // Don't show modal on login page
        if (pathname === "/login" || pathname === "/") {
          if (showModalRef.current) setShowModal(false);
          return;
        }

        const currentTime = Date.now() / 1000;
        const timeUntilExpiry = decoded.exp - currentTime;

        if (timeUntilExpiry <= 0) {
          // Token expired — log out
          clearSessionRef.current?.();
          return;
        }

        if (timeUntilExpiry <= WARNING_SECONDS) {
          const expKey = decoded.exp;
          if (modalShownForExpRef.current !== expKey) {
            setShowModal(true);
            modalShownForExpRef.current = expKey;
          }
        }

        setTimeRemaining(Math.max(0, timeUntilExpiry));
      } catch (e) {
        // Invalid token — log out
        clearSessionRef.current?.();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []); // ⬅️ Empty deps — interval is stable for the entire component lifetime

  const handleContinue = async () => {
    setIsRefreshing(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/user/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      localStorage.setItem("token", response.data.authToken);
      setShowModal(false);
      modalShownForExpRef.current = null;
    } catch (error) {
      // Don't logout immediately — just let the countdown continue
      // to auto-logout at expiry if the token truly expires
      console.error("Session refresh failed:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!showModal) return null;

  const minutes = Math.floor(timeRemaining / 60);
  const seconds = Math.floor(timeRemaining % 60);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900 bg-opacity-75">
      <div className="bg-white rounded-xl p-6 w-96 shadow-2xl border border-gray-200">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center justify-center w-10 h-10 bg-yellow-100 rounded-full">
            <svg
              className="w-5 h-5 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-gray-800">Session Expiring</h3>
        </div>

        <p className="text-gray-600 mb-2">
          Your session will expire in{" "}
          <span className="font-semibold text-gray-800">
            {minutes}:{seconds.toString().padStart(2, "0")}
          </span>
          .
        </p>
        <p className="text-sm text-gray-500 mb-6">
          Do you want to continue your session?
        </p>

        {/* Progress bar */}
        <div className="w-full h-2 bg-gray-200 rounded-full mb-6 overflow-hidden">
          <div
            className="h-full bg-yellow-500 rounded-full transition-all duration-1000 ease-linear"
            style={{ width: `${(timeRemaining / WARNING_SECONDS) * 100}%` }}
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={() => clearSessionRef.current?.()}
            disabled={isRefreshing}
            className="px-4 py-2 text-sm font-medium text-gray-600 transition-colors bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-800 disabled:opacity-50"
          >
            Logout
          </button>
          <button
            onClick={handleContinue}
            disabled={isRefreshing}
            className="px-5 py-2 text-sm font-medium text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isRefreshing ? "Refreshing..." : "Continue Session"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutModal;
